import React from 'react';

import Typography, { TypographyProps } from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

type FontWeight =
  | 'normal'
  | 'bold'
  | 'bolder'
  | 'lighter'
  | 100
  | 200
  | 300
  | 400
  | 500
  | 600
  | 700
  | 800
  | 900
  | 'inherit'
  | 'initial'
  | 'unset';

export type Kind = 'muted' | 'price' | 'inline';

type ComponentType =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'span'
  | 'div'
  | 'p';

export interface Props extends TypographyProps {
  fw?: FontWeight;
  kind?: Kind;
  italic?: boolean;
  component?: ComponentType;
}

const useStyles = makeStyles()(theme => ({
  italic: {
    fontStyle: 'italic',
  },
  'fw-normal': {
    fontWeight: 'normal',
  },
  'fw-bold': {
    fontWeight: 'bold',
  },
  'fw-bolder': {
    fontWeight: 'bolder',
  },
  'fw-lighter': {
    fontWeight: 'lighter',
  },
  'fw-100': {
    fontWeight: 100,
  },
  'fw-200': {
    fontWeight: 200,
  },
  'fw-300': {
    fontWeight: 300,
  },
  'fw-400': {
    fontWeight: 400,
  },
  'fw-500': {
    fontWeight: 500,
  },
  'fw-600': {
    fontWeight: 600,
  },
  'fw-700': {
    fontWeight: 700,
  },
  'fw-800': {
    fontWeight: 800,
  },
  'fw-900': {
    fontWeight: 900,
  },
  'fw-inherit': {
    fontWeight: 'inherit',
  },
  'fw-initial': {
    fontWeight: 'initial',
  },
  'fw-unset': {
    fontWeight: 'unset',
  },
  'kind-muted': {
    color: theme.palette.grey[500],
  },
  'kind-price': {
    color: theme.palette.secondary.main,
  },
  'kind-inline': {
    display: 'contents',
  },
}));

const Text: React.FC<Props> = ({
  children,
  className,
  kind,
  fw,
  italic,
  ...props
}) => {
  const { classes, cx } = useStyles();
  return (
    <Typography
      {...props}
      className={cx(className, {
        [classes[`fw-${fw}` as keyof typeof classes]]: Boolean(fw),
        [classes[`kind-${kind}` as keyof typeof classes]]: Boolean(kind),
        [classes.italic]: italic,
      })}
    >
      {children}
    </Typography>
  );
};

export default Text;
